export interface ResourceCacheOptions {
  maxEntries?: number;
  now?: () => number;
}

export interface ResourceReadOptions<T> {
  key: string;
  loader: (signal: AbortSignal) => Promise<T>;
  onRevalidated?: (value: T) => void;
  scope: string;
  signal?: AbortSignal;
  staleTtlMs?: number;
  ttlMs: number;
}

export interface ResourceSnapshot<T> {
  freshness: "fresh" | "stale";
  updatedAt: number;
  value: T;
}

interface CacheEntry {
  accessOrder: number;
  updatedAt: number;
  value: unknown;
}

interface InFlightRequest {
  controller: AbortController;
  promise: Promise<unknown>;
}

function abortError(): DOMException {
  return new DOMException("The resource request was aborted.", "AbortError");
}

function scopedKey(scope: string, key: string): string {
  return `${scope.length}:${scope}${key}`;
}

function withCallerAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      cleanup();
      reject(abortError());
    };
    const cleanup = () => signal.removeEventListener("abort", abort);
    signal.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (caught: unknown) => {
        cleanup();
        reject(caught);
      },
    );
  });
}

export class ResourceCache {
  private readonly entries = new Map<string, CacheEntry>();
  private readonly inFlight = new Map<string, InFlightRequest>();
  private readonly maxEntries: number;
  private readonly now: () => number;
  private accessOrder = 0;

  constructor(options: ResourceCacheOptions = {}) {
    this.maxEntries = Math.max(1, Math.trunc(options.maxEntries ?? 32));
    this.now = options.now ?? Date.now;
  }

  peek<T>(scope: string, key: string, ttlMs: number): ResourceSnapshot<T> | undefined {
    const entry = this.entries.get(scopedKey(scope, key));
    if (!entry) return undefined;
    return {
      freshness: this.now() - entry.updatedAt <= ttlMs ? "fresh" : "stale",
      updatedAt: entry.updatedAt,
      value: entry.value as T,
    };
  }

  read<T>(options: ResourceReadOptions<T>): Promise<T> {
    const compositeKey = scopedKey(options.scope, options.key);
    const entry = this.entries.get(compositeKey);
    const age = entry ? this.now() - entry.updatedAt : Number.POSITIVE_INFINITY;
    const staleTtlMs = Math.max(options.ttlMs, options.staleTtlMs ?? options.ttlMs * 4);

    if (entry && age <= options.ttlMs) {
      this.touch(entry);
      return withCallerAbort(Promise.resolve(entry.value as T), options.signal);
    }

    if (entry && age <= staleTtlMs) {
      this.touch(entry);
      void this.refresh(compositeKey, options.loader)
        .then((value) => options.onRevalidated?.(value))
        .catch(() => undefined);
      return withCallerAbort(Promise.resolve(entry.value as T), options.signal);
    }

    return withCallerAbort(this.refresh<T>(compositeKey, options.loader), options.signal);
  }

  invalidate(scope: string, key?: string): void {
    const exactKey = key === undefined ? null : scopedKey(scope, key);
    const scopePrefix = `${scope.length}:${scope}`;
    for (const compositeKey of this.entries.keys()) {
      if (exactKey ? compositeKey === exactKey : compositeKey.startsWith(scopePrefix)) {
        this.entries.delete(compositeKey);
      }
    }
    for (const [compositeKey, request] of this.inFlight) {
      if (exactKey ? compositeKey === exactKey : compositeKey.startsWith(scopePrefix)) {
        request.controller.abort();
        this.inFlight.delete(compositeKey);
      }
    }
  }

  clear(): void {
    this.entries.clear();
    for (const request of this.inFlight.values()) request.controller.abort();
    this.inFlight.clear();
  }

  private touch(entry: CacheEntry): void {
    entry.accessOrder = ++this.accessOrder;
  }

  private refresh<T>(compositeKey: string, loader: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const existing = this.inFlight.get(compositeKey);
    if (existing) return existing.promise as Promise<T>;

    const controller = new AbortController();
    const request = Promise.resolve()
      .then(() => loader(controller.signal))
      .then((value) => {
        if (this.inFlight.get(compositeKey)?.controller === controller && !controller.signal.aborted) {
          this.entries.set(compositeKey, {
            accessOrder: ++this.accessOrder,
            updatedAt: this.now(),
            value,
          });
          this.evictOverflow();
        }
        return value;
      })
      .finally(() => {
        if (this.inFlight.get(compositeKey)?.controller === controller) {
          this.inFlight.delete(compositeKey);
        }
      });
    this.inFlight.set(compositeKey, { controller, promise: request });
    return request;
  }

  private evictOverflow(): void {
    while (this.entries.size > this.maxEntries) {
      let oldestKey: string | undefined;
      let oldestOrder = Number.POSITIVE_INFINITY;
      for (const [key, entry] of this.entries) {
        if (entry.accessOrder < oldestOrder) {
          oldestKey = key;
          oldestOrder = entry.accessOrder;
        }
      }
      if (oldestKey === undefined) return;
      this.entries.delete(oldestKey);
    }
  }
}

export const resourceCache = new ResourceCache();
